import React, { useState, useContext } from "react";
import { toast } from "react-toastify";
import { showSuccess, showError } from "../../utils/toast";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputUI from "../material-ui/InputUI";
import ButtonUI from "../material-ui/ButtonUI";
import { AuthContext } from "../../context/Auth/AuthContext";
import addRole from "../../api/addRole";
import {
  permissionDependencies,
  documentsDeps,
} from "../../utils/permissionConfig";
import "../../styles/AddDocument.css";
import CloseIcon from "@mui/icons-material/Close";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PermissionSections from "./PermissionSections";

const addRoleFormSchema = yup.object().shape({
  name: yup.string().required("Role Name is a required field"),
  permissions: yup
    .array()
    .min(1, "Please select at least one permission")
    .required("Please select permissions"),
  status: yup.string().required("Status is required field"),
});

const AddRole = ({ onAddRoleButtonClick, setRoleAddedToTrue }) => {
  const [loading, setLoading] = useState(false);
  const { token } = useContext(AuthContext);

  const statusOptions = [
    { name: "Active", value: "active" },
    { name: "Inactive", value: "inactive" },
  ];

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(addRoleFormSchema),
    defaultValues: { permissions: [] },
  });

  const permissions = watch("permissions");

  const handleCategoryChange = (category, isChecked) => {
    let newPermissions = [...permissions];
    const categoryValues = category.children.map((child) => child.value);
    let additionalDeps = [];

    if (category.name === "Mail Room") {
      additionalDeps = permissionDependencies["view_documents"] || [];
    }

    if (category.name === "Settings") {
      additionalDeps = [
        "view_users",
        "create_users",
        "edit_users",
        "delete_users",
        "view_field_settings",
        "create_field_settings",
        "edit_field_settings",
        "delete_field_settings",
        "view_document_requests",
        "create_document_requests",
        "edit_document_requests",
        "delete_document_requests",
      ];
    }

    if (category.name === "Delete") {
      additionalDeps = [
        "view_trash_users",
        "delete_users_permanently",
        "restore_users",
        "view_trash_documents",
        "delete_documents_permanently",
        "restore_documents",
      ];
    }

    if (category.name === "All Documents" && !isChecked) {
      const toRemove = [
        ...categoryValues,
        "view_documents",
        "view_all_assigned",
        "view_all_unassigned",
        "view_my_assigned",
        ...documentsDeps,
      ];
      newPermissions = newPermissions.filter(
        (perm) => !toRemove.includes(perm)
      );
    } else if (isChecked) {
      newPermissions = [
        ...new Set([...newPermissions, ...categoryValues, ...additionalDeps]),
      ];
    } else {
      newPermissions = newPermissions.filter(
        (perm) =>
          !categoryValues.includes(perm) && !additionalDeps.includes(perm)
      );
    }

    setValue("permissions", newPermissions);
  };

  const handlePermissionChange = (permission) => {
    const isChecked = permissions.includes(permission.value);
    const dependencies = permissionDependencies[permission.value] || [];

    const updatedPermissions = isChecked
      ? permissions.filter(
          (p) => p !== permission.value && !dependencies.includes(p)
        )
      : [...new Set([...permissions, permission.value, ...dependencies])];

    setValue("permissions", updatedPermissions);
  };

  const onAddRoleFormSubmit = async (addRoleFormData) => {
    setLoading(true);
    try {
      const response = await addRole({
        token,
        name: addRoleFormData.name,
        permissions: addRoleFormData.permissions,
        status: addRoleFormData.status,
      });

      if (response?.code === 200) {
        showSuccess("Role is added successfully");
        onAddRoleButtonClick();
        setRoleAddedToTrue();
      } else {
        showError("Error while adding role");
      }
    } catch {
      showError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-document-page h-auto">
      <div className="flex w-full max-w-[1400px] flex-col gap-3 px-3 sm:px-4">
        {/* Role name + status */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#e3f5f8] text-[#1a6b7c]">
                <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 26 }} />
              </span>
              <div className="min-w-0">
                <div className="text-xl font-bold leading-tight text-gray-900">Add New Role</div>
                <div className="text-[13px] text-gray-500">Set a role name and assign the required permissions</div>
              </div>
            </div>
            <button
              type="button"
              onClick={onAddRoleButtonClick}
              aria-label="Close"
              title="Close"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(220px,340px)]">
            <div className="min-w-0">
              <div className="mb-1 text-[13px] font-semibold text-gray-800">
                Role Name <span className="text-red-500">*</span>
              </div>
              <InputUI
                name="name"
                type="text"
                variant="outlined"
                fullWidth
                placeholder="Enter role name (e.g. Document Viewer, Document Manager)"
                sx={{
                  "& .MuiOutlinedInput-root": { height: "40px", borderRadius: "8px", backgroundColor: "#fff", fontSize: "14px" },
                  "& fieldset": { borderColor: "#e5e7eb" },
                }}
                register={register}
              />
              <span className="error-message">{errors["name"]?.message}</span>
            </div>
            <div className="min-w-0">
              <div className="mb-1 text-[13px] font-semibold text-gray-800">Status</div>
              <FormControl fullWidth>
                <Controller
                  control={control}
                  name="status"
                  defaultValue="active"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="status"
                      variant="outlined"
                      value={value}
                      onChange={onChange}
                      renderValue={(selected) => (
                      <span className="flex items-center gap-2">
                        <span
                          className={`h-2.5 w-2.5 rounded-full ${
                            selected === "active" ? "bg-emerald-500" : "bg-gray-400"
                          }`}
                        />
                        {statusOptions.find((o) => o.value === selected)?.name || selected}
                      </span>
                    )}
                      sx={{
                      height: "40px",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      fontSize: "14px",
                      "& fieldset": { borderColor: "#e5e7eb" },
                    }}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
              <span className="error-message">{errors["status"]?.message}</span>
            </div>
          </div>
        </div>

        {/* Permissions (same permission state + handlers as before) */}
        <PermissionSections
          permissions={permissions}
          onCategoryChange={handleCategoryChange}
          onPermissionChange={handlePermissionChange}
          error={errors["permissions"]?.message}
        />

        {/* Footer actions: centered on a white bar */}
        <div className="mb-2 flex flex-wrap items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <ButtonUI
            formButton={false}
            variant="outlined"
            buttonText="Cancel"
            style={{
              height: "40px",
              minWidth: "110px",
              color: "#374151",
              backgroundColor: "#f3f4f6",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              boxShadow: "none",
              fontWeight: 600,
            }}
            onClickFunc={onAddRoleButtonClick}
          />
          <ButtonUI
            loading={loading}
            variant="contained"
            buttonText={
              <span className="flex items-center gap-1.5">
                <LockOutlinedIcon sx={{ fontSize: 18 }} />
                Create Role
              </span>
            }
            style={{
              height: "40px",
              minWidth: "150px",
              color: "#fff",
              backgroundColor: "#1a6b7c",
              boxShadow: "none",
              borderRadius: "8px",
              fontWeight: 600,
            }}
            handleSubmit={handleSubmit}
            onFormSubmit={onAddRoleFormSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default AddRole;
